/**
 * Vuex module for call queues.
 */
import debug from 'debug'
import API from '@/api'
import events from 'Store/events'
import User from 'Store/class/user'
import { ParsePresXML } from 'Store/class/helper'

const dbg = debug('store:action:users:')

const actions = {
  initUsers ({ commit, state }) {
    dbg('Init application users')
    return API.getPanelUsers(0, state.pagination.perPage).then(users => {
      // reset users when page is reload after connection lost
      // but page is not reloaded
      commit('USERS_RESET')
      users.data.data.forEach(user => {
        commit('USER_ADD', user)
      })
      commit('INIT_PAGINATION', users.data.total)
    })
  },

  initBuddies ({ commit }) {
    dbg('Init application buddies')
    return API.getPanelBuddies().then(buddies => {
      commit('BUDDIES_RESET')
      buddies.data.data.forEach(buddy => {
        commit('INIT_BUDDY', buddy)
      })
      commit('BUDDIES_SORT')
    })
  },

  setFilter ({ commit }, filter) {
    commit('SET_FILTER', filter)
  },

  getAllUsers ({ commit, state }) {
    if (process.env.NODE_ENV === 'test') {
      return true
    }
    return new Promise((resolve, reject) => {
      return API.getPanelUsers(0, 999).then(resp => {
        const users = resp.data.data.map(user => new User(user))
        commit('USER_ADD_ALL', users)
        resolve()
      }).catch(err => {
        const rej = err?.response?.data || { type: 'Error', exception: 'Failed to fetch users from the API.' }
        reject(rej)
      })
    })
  },

  searchUsers ({ commit, state }, page) {
    commit('USERS_TO_PAGE', page)
    return new Promise((resolve, reject) => {
      const page = state.pagination
      return API.getPanelUsers(0, page.perPage, page.filter)
        .then(resp => {
          commit('USERS_RESET')
          resp.data.data.forEach(user => {
            commit('USER_ADD', user)
          })
          commit('INIT_PAGINATION', resp.data.total)
          resolve()
        }).catch(err => {
          const rej = err?.response?.data || { type: 'Error', exception: 'Failed to fetch users from the API.' }
          reject(rej)
        })
    })
  },

  pageLoad ({ commit, state }, page) {
    commit('USERS_TO_PAGE', page)
    return new Promise((resolve, reject) => {
      const page = state.pagination
      return API.getPanelUsers(page.start, page.perPage, page.filter)
        .then(resp => {
          commit('USERS_RESET')
          resp.data.data.forEach(user => {
            commit('USER_ADD', user)
          })
          resolve()
        }).catch(err => {
          const rej = err?.response?.data || { type: 'Error', exception: 'Failed to fetch users from the API.' }
          reject(rej)
        })
    })
  },

  buddyMoved: function ({ commit }, ev) {
    const buddy = ev.moved.element
    const newSort = ev.moved.newIndex
    const oldSort = ev.moved.oldIndex
    commit('BUDDY_MOVE', { buddy, newSort, oldSort })
    commit('BUDDIES_SORT')
    API.updateBuddy(buddy.exten, newSort).catch(err => {
      events.pubError(API.getException(err) ||
        'Failed to update buddy card with API.')
      dbg(err)
    })
  },

  startWatchUser ({ commit }, user) {
    commit('BUDDY_APPEND', user)
  },

  stopWatchBuddy ({ commit }, buddy) {
    commit('BUDDY_REMOVE', buddy)
  },

  parking ({ commit }, data) {
    const event = data.event?.toUpperCase()
    switch (event) {
      case 'PARKEDCALL':
        dbg(`New parked call to ${data.parkeecalleridnum}`)
        commit('NEW_PARKED_CALL', data)
        break
      case 'UNPARKEDCALL':
        commit('PARKED_CALL_UNPARK', data)
        break
      case 'PARKEDCALLGIVEUP':
      case 'PARKEDCALLTIMEOUT':
        data['call-id'] = data.parkeechanvariable?.SIPCALLID
        dbg(`Terminated parked call ${data.parkeecalleridnum}, slot: ${data.parkingspace}`)
        commit('CALL_TERMINATED', data)
        break
      case 'PARKEDCALLSWAP':
        dbg(`Swap parked call for ${data.parkeecalleridnum}, slot: ${data.parkingspace}`)
        commit('PARKED_CALL_SWAP', data)
        break
      default:
        dbg(`Unknown parking event ${event}. Ignore.`)
        break
    }
  }
}

const mutations = {
  INIT_BUDDY (state, buddy) {
    const user = new User(buddy)
    user.setBuddy(buddy.sort)
    state.buddies.push(user)
  },

  USER_ADD_ALL (state, data) {
    state.usersAll = data
  },

  USER_ADD (state, data) {
    const user = new User(data)
    if (!User.exists(state.users, user)) {
      state.users.push(user)
    }
  },

  USERS_RESET (state) {
    state.users.splice(0)
  },

  BUDDIES_RESET (state) {
    state.buddies.splice(0)
  },

  INIT_PAGINATION (state, total) {
    state.pagination.total = total
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.curPage = 1
    state.pagination.start = 0
    state.pagination.limit = state.pagination.perPage
  },

  SET_FILTER (state, filter) {
    state.pagination.filter = filter
  },

  USERS_TO_PAGE (state, page) {
    state.pagination.curPage = page
    state.pagination.start = (page - 1) * state.pagination.perPage
  },

  BUDDY_APPEND (state, user) {
    const max = Math.max(...state.buddies.map(b => b.buddy_sort))
    const sort = max >= 0 ? max + 1 : 0
    user.setBuddy(sort)
    state.buddies.push(user)
    API.addBuddy(user.exten, sort).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to add buddy with API')
    })
  },

  BUDDY_REMOVE (state, buddy) {
    const idx = state.buddies.findIndex(b => b.exten === buddy.exten)
    if (idx !== -1) {
      state.buddies.splice(idx, 1)
    }
    // reindex sort
    for (let i = buddy.buddy_sort; i < state.buddies.length; i++) {
      state.buddies[i].buddy_sort = i
    }
    API.removeBuddy(buddy.exten).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to remove buddy with API')
    })
  },

  BUDDY_MOVE (state, { buddy, newSort, oldSort }) {
    const from = newSort < oldSort ? newSort : oldSort
    const to = newSort < oldSort ? oldSort : newSort
    for (let i = from; i <= to; i++) {
      if (state.buddies[i] === buddy) {
        state.buddies[i].buddy_sort = newSort
      } else {
        const sort = ~~state.buddies[i]?.buddy_sort
        if (state.buddies[i]) {
          state.buddies[i].buddy_sort = newSort < oldSort
            ? sort + 1
            : sort - 1
        }
      }
    }
  },

  BUDDIES_SORT (state) {
    state.buddies.sort((a, b) => a.buddy_sort - b.buddy_sort)
  },

  CALL_INITIATE (state, data) {
    state.buddies.forEach((buddy) => {
      if (buddy.exten === data.touser ||
          buddy.exten === User.fromuserExten(data.fromuser)) {
        buddy.callInit(data)
      }
    })
  },

  CALL_CONFIRMED (state, data) {
    const buddies = state.buddies.filter((b) =>
      b.calls.some((c) => c.callid === data['call-id'] && !c.isConfirmed))

    buddies.forEach((buddy) => {
      buddy.callConfirmed(data)
    })
  },

  CALL_SET_ASTCHAN (state, data) {
    state.buddies.forEach(buddy => buddy.calls.forEach(call => {
      if (
        call.callid !== data.chanvariable?.SIPCALLID &&
        call.legClid !== data.chanvariable?.SIPCALLID
      ) {
        return
      }
      if (call.user === data.calleridnum) {
        call.srcAstchan = data.channel
      } else {
        call.astchan = data.channel
      }
    }))
  },

  ATTXFER_CALLS_SET (state, data) {
    const xferUser = state.buddies.find(b => b.exten === data.transfereecalleridnum)
    if (xferUser) {
      // init transfee call
      xferUser.addAttXferTargetCall(data)
    }
    const xferTarget = state.buddies.find(b => b.exten === data.transfertargetcalleridnum)
    if (xferTarget) {
      // init target call
      xferTarget.addAttXferUserCall(data)
    }
  },

  CALL_ASTCHAN (state, data) {
    const callid = data?.chanvariable?.SIPCALLID
    if (!callid) {
      dbg('commit CALL_ASTCHAN: callid is not found: %o', data)
      return
    }
    const buddies = state.buddies.filter((b) =>
      b.calls.some((c) => c.callid === callid))

    if (!buddies.length) {
      dbg(`commit CALL_ASTCHAN: No active calls with callid '${callid}'`)
      return
    }
    buddies.forEach((buddy) => {
      buddy.updateCallChannel(data, callid)
    })
  },

  SET_BARGEIN_CALL (state, data) {
    const callid = data.spyeechanvariable?.SIPCALLID
    if (!callid) {
      dbg('commit SET_BARGEIN_CALL: callid is not found: %o', data)
      return
    }
    state.buddies.forEach(buddy => buddy.calls.forEach(call => {
      if (call.callid === callid || call.legClid === callid) {
        call.setBargein(data)
      }
    }))
  },

  CALL_TERMINATED (state, data) {
    state.buddies.forEach(buddy => {
      if (buddy.calls.length > 0) {
        const idx = buddy.calls.findIndex((c) => {
          return c.callid === data['call-id'] || c.legClid === data['call-id']
        })
        if (idx !== -1) {
          buddy.callTerminated(idx)
        }
      }
    })
  },

  PRESENCE_SET (state, data) {
    const user = User.findByExten(state.buddies, data.touser)
    if (!user) {
      return
    }

    if (data.event === 'message-summary') {
      user.updateVmail(data)
    } else {
      user.setPresence(data)
    }
  },

  MWI_UPDATE (state, data) {
    const exten = data.mailbox?.split('@')[0]
    if (!exten) return
    const user = User.findByExten(state.buddies, exten)
    if (!user) return
    user.vmnum = ~~data.new
  },

  FEATURE_SET (state, data) {
    const { error, remote, state: dlgState } = ParsePresXML(data.body)
    if (error) {
      dbg(`Failed to buddy feature ${data.featureName}: ${error}`)
      return
    }
    // Clean up remote.user if it starts with a feature code
    if (remote && remote.user && data.featureCodes) {
      const codes = Object.values(data.featureCodes)
      const matchedCode = codes.find(code => remote.user.startsWith(code))
      if (matchedCode) {
        remote.user = remote.user.slice(matchedCode.length)
      }
    }
    const buddy = state.buddies.find(b => b.exten === remote.user)
    if (!buddy) {
      dbg(`Failed to find feature buddy ${remote.user}`)
      return
    }
    const isOn = dlgState === 'confirmed'
    switch (data.featureName) {
      case 'dnd':
        buddy.isDND = isOn
        break
      case 'forward':
        buddy.hasFwd = isOn
        break
      case 'followme':
        buddy.hasFme = isOn
        break
      default:
        dbg(`Unknown presence feature '${data.featureName}'`)
        break
    }
  },

  NEW_PARKED_CALL (state, data) {
    for (const buddy of state.buddies) {
      if (buddy.exten === data.parkeecalleridnum) {
        // make sure ignore duplicated ParkedCall events
        const parkCallID = data.parkeechanvariable?.SIPCALLID
        if (buddy.calls.find(({ callid }) => callid === parkCallID)) {
          continue
        }
        buddy.setParked(data)
        break
      }
    }
  },

  PARKED_CALL_UNPARK (state, data) {
    // terminate parked call
    const parkeeCID = data.parkeechanvariable?.SIPCALLID
    const retrievCID = data.retrieverchanvariable?.SIPCALLID
    for (const buddy of state.buddies) {
      const idx = buddy.calls.findIndex((c) => {
        return c.callid === parkeeCID || c.legClid === retrievCID
      })
      if (idx !== -1) {
        buddy.callTerminated(idx)
      }
    }

    // setup parker and parkee on call
    for (const buddy of state.buddies) {
      // avoid duplicated calls with AMI event retransmissions
      if (buddy.hasCall(data.parkeechanvariable?.SIPCALLID) ||
        buddy.hasCall(data.retrieverchanvariable?.SIPCALLID)
      ) continue

      if (buddy.exten === data.parkeecalleridnum) {
        buddy.setUnparkedRetrieveCall(data)
        continue
      }

      if (buddy.exten === data.retrievercalleridnum) {
        buddy.setUnparkedParkeeCall(data)
      }
    }
  },

  PARKED_CALL_SWAP (state, data) {
    for (const buddy of state.buddies) {
      // hangup old chan
      for (const idx in buddy.calls) {
        const call = buddy.calls[idx]
        if (call.isParked && call.user === data.parkingspace) {
          buddy.callTerminated(idx)
          break
        }
      }
      // spawn new - add parking call to new exten
      if (buddy.exten === data.parkeecalleridnum) {
        buddy.setParked(data)
      }
    }
  },

  SET_PREFER (state, data) {
    const user = User.findByExten(state.buddies, data.username)
    if (!user) {
      dbg(`User '${data.username}' not found in buddies list`)
      return
    }
    user.setPrefs(data)
  }
}

const state = {
  users: [],
  usersAll: [], // all users, no paginate
  buddies: [],
  compactView: false,
  superLightView: false,
  sort: 'custom',
  superLightSize: 48,
  pagination: {
    filter: null,
    perPage: 18,
    pages: 0,
    curPage: 0,
    start: 0,
    total: 0
  }
}

const getters = {
  list: state => state.users,
  listAll: state => state.usersAll,
  buddies: state => state.buddies,
  pagination: state => state.pagination,
  totalUsers: state => state.pagination.totalUsers,
  compactView: state => state.compactView,
  superLightView: state => state.superLightView,
  superLightSize: state => state.superLightSize,
  sort: state => state.sort,
  isUserBuddy: state => user => state.buddies.findIndex(b => b.exten === user.exten) !== -1
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
